import { CalendarDays, Loader2 } from "lucide-react";
import { THC } from "@/lib/tankHealthCheck";

export function BookingPanel({
  days, selectedDate, onDate, selectedSlot, onSlot, contact, onContact,
  address, onEditAddress, locked, submitting, onSubmit, error, loadingSlots,
}) {
  const day = days.find((d) => d.date === selectedDate);
  return (
    <section id="book" className="mt-9 md:mt-14 rounded-[16px] border p-[18px] md:p-7"
      style={{ borderColor: THC.border, backgroundColor: "#FFFFFF", boxShadow: THC.shadow }}
      data-testid="thc-booking-panel">
      <div className="lg:grid lg:grid-cols-[minmax(0,36%)_minmax(0,36%)_minmax(0,28%)] lg:gap-6">
        <div>
          <h2 className="font-extrabold uppercase leading-[1.05] text-[29px] md:text-[32px] xl:text-[35px]"
            style={{ color: THC.navy, fontFamily: "'Outfit',system-ui,sans-serif" }}>
            BOOK YOUR COMPLIMENTARY HEALTH CHECK
          </h2>
          <p className="mt-2 text-[15.5px] md:text-[16px] leading-[1.5]" style={{ color: THC.body }}>
            Select a time that works for you. The onsite Health Check takes about 30 minutes.
          </p>
          <p className="mt-3 inline-block rounded-[10px] px-3 py-1.5 text-[14px] font-black uppercase tracking-wide"
            style={{ backgroundColor: THC.yellow, color: THC.navy }} data-testid="thc-booking-owner-note">
            OWNER MUST BE PRESENT
          </p>
        </div>

        <div className={`mt-5 lg:mt-0 ${locked ? "opacity-50 pointer-events-none" : ""}`}
          data-testid="thc-slot-picker" aria-disabled={locked}>
          <label htmlFor="thc-date" className="flex items-center gap-2 text-[14px] font-bold uppercase tracking-wide"
            style={{ color: THC.navy }}>
            <CalendarDays className="w-4 h-4" aria-hidden="true" /> Select a date
          </label>
          <select id="thc-date" data-testid="thc-date-select" value={selectedDate}
            onChange={(e) => onDate(e.target.value)} disabled={locked}
            className="mt-1.5 w-full h-[50px] rounded-[8px] border px-3 text-[16px] bg-white"
            style={{ borderColor: THC.border, color: THC.body }}>
            <option value="">{loadingSlots ? "Loading available dates…" : "Choose a date…"}</option>
            {days.map((d) => <option key={d.date} value={d.date}>{d.date_display}</option>)}
          </select>

          <div className="mt-3 text-[14px] font-bold uppercase tracking-wide" style={{ color: THC.navy }}>Select a time</div>
          <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="thc-slot-grid">
            {(day?.times || []).slice(0, 18).map((t) => {
              const active = selectedSlot?.id === t.id && selectedSlot?.start_time === t.start_time;
              return (
                <button key={`${t.id}-${t.start_time}`} onClick={() => onSlot(t)}
                  data-testid={`thc-time-${t.start_time}`}
                  className="min-h-[44px] min-w-[86px] rounded-[8px] border text-[15px] font-semibold transition-colors"
                  style={{
                    borderColor: active ? THC.blue : THC.border,
                    backgroundColor: active ? THC.blue : THC.faint,
                    color: active ? "#FFFFFF" : THC.body,
                  }}>
                  {t.start_display}
                </button>
              );
            })}
            {!day && <p className="col-span-full text-[14px]" style={{ color: THC.muted }}>Choose a date to see available times.</p>}
          </div>
          {/* Jun 2026 — the customer books an ARRIVAL WINDOW, not a
              guaranteed time. Shown the moment a time is selected. */}
          {selectedSlot?.arrival_window_display && (
            <div className="mt-3 rounded-[8px] border px-3 py-2.5"
              style={{ borderColor: THC.blue, backgroundColor: THC.faint }}
              data-testid="thc-arrival-window">
              <div className="text-[14px] font-bold" style={{ color: THC.navy }}>
                Your booked arrival window will be {selectedSlot.arrival_window_display}.
              </div>
              <p className="mt-1 text-[13px] leading-[1.45]" style={{ color: THC.muted }}>
                Our technicians work across multiple properties each day, so arrival times can
                occasionally change due to travel, previous jobs or unexpected delays. If your
                arrival window needs to change, we’ll endeavour to give you as much notice as
                possible. We’ll also send you a reminder by text and email the day before.
              </p>
            </div>
          )}
        </div>

        <div className={`mt-5 lg:mt-0 ${locked ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="grid grid-cols-2 gap-2">
            <Field id="first_name" label="First name" value={contact.first_name} onChange={onContact} />
            <Field id="last_name" label="Last name" value={contact.last_name} onChange={onContact} />
            <Field id="email" label="Email" type="email" value={contact.email} onChange={onContact} className="col-span-2" />
            <Field id="mobile" label="Mobile" type="tel" value={contact.mobile} onChange={onContact} className="col-span-2" />
          </div>
          <p className="mt-2 text-[13.5px] leading-[1.45]" style={{ color: THC.muted }}
            data-testid="thc-partner-note">
            Your complimentary onsite Tank Water Health Check will be conducted by our
            service partner, Pristine Water Systems.
          </p>
          {/* The hero PROPERTY ADDRESS is the single source of truth — the
              customer never types their address twice. */}
          <div className="mt-2 rounded-[8px] border px-3 py-2" style={{ borderColor: THC.border, backgroundColor: THC.faint }}
            data-testid="thc-booking-address-summary">
            <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: THC.muted }}>
              PROPERTY ADDRESS
            </div>
            <div className="text-[14px] font-bold leading-snug" style={{ color: THC.navy }}>
              {address.property_address || "Add your address in the PROPERTY ADDRESS field above"}
            </div>
            {address.address_verified && (
              <button type="button" onClick={onEditAddress} data-testid="thc-edit-address"
                className="mt-0.5 text-[12px] font-semibold underline" style={{ color: THC.blue }}>
                Change address
              </button>
            )}
          </div>
          {error && (
            <p className="mt-2 text-[14px] font-semibold" style={{ color: THC.red }} data-testid="thc-booking-error" role="alert">
              {error}
            </p>
          )}
          <button onClick={onSubmit} disabled={submitting || locked} data-testid="thc-submit-booking"
            className="mt-3 w-full lg:min-w-[300px] h-[56px] rounded-[8px] text-white text-[16px] md:text-[17px] font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: THC.blue }}>
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> : <CalendarDays className="w-5 h-5" aria-hidden="true" />}
            BOOK MY COMPLIMENTARY HEALTH CHECK
          </button>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, value, onChange, type = "text", className = "" }) {
  return (
    <div className={className}>
      <label htmlFor={`thc-${id}`} className="block text-[12.5px] font-bold uppercase tracking-wide" style={{ color: THC.navy }}>
        {label}
      </label>
      <input id={`thc-${id}`} data-testid={`thc-field-${id}`} type={type} value={value || ""}
        onChange={(e) => onChange({ [id]: e.target.value })}
        className="mt-1 w-full h-[44px] rounded-[8px] border px-3 text-[15.5px] outline-none focus:ring-2 focus:ring-[#09A9E6]"
        style={{ borderColor: THC.border, color: THC.body, backgroundColor: THC.faint }} />
    </div>
  );
}
